import { Divider, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Switch } from '@/components/form/baseInputs/Switch';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { HeaderCard } from '@/components/layout/HeaderCard';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useReportError } from '@/hooks/useReportError';
import { TenantOrgMembershipType } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { normalizeEmailDomain } from './AdminOrganizationsPage.utils';
import { CreateOrganizationMemberTenantModal } from './CreateOrganizationMemberTenantModal';
import {
  AdminOrganizationsPage_LuminaryOrganizationFragment,
  AdminOrganizationsPageQuery,
  Operations,
  useAdminOrganizationsPageAddWorkOsOrganizationDomainMutation,
  useAdminOrganizationsPageDeleteWorkOsOrganizationDomainMutation,
  useAdminOrganizationsPageSetLuminaryOrganizationJitMutation,
  useAdminOrganizationsPageUpdateWorkOsOrganizationMutation,
} from './graphql/AdminOrganizationsPage.generated';
import { OrganizationConfigurationForm } from './OrganizationConfigurationForm/OrganizationConfigurationForm';
import { OrganizationPresentationSettingsForm } from './OrganizationConfigurationForm/OrganizationPresentationSettingsForm';
import { OrganizationDetailsList } from './OrganizationDetailsList';

type TenantSummary = AdminOrganizationsPageQuery['adminListTenants'][number];
type Organization = AdminOrganizationsPage_LuminaryOrganizationFragment;

interface OrganizationDetailsProps {
  canEdit: boolean;
  organization: Organization;
  tenants: TenantSummary[];
}

interface PendingRemoval {
  id: string;
  label: string;
}

function useServerSyncedInput(serverValue: string) {
  const [lastServerValue, setLastServerValue] = useState(serverValue);
  const [value, setValue] = useState(serverValue);
  if (serverValue !== lastServerValue) {
    setLastServerValue(serverValue);
    setValue(serverValue);
  }
  return [value, setValue] as const;
}

export function OrganizationDetails({
  canEdit,
  organization,
  tenants,
}: OrganizationDetailsProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const domains = [...getNodes(organization.workosOrganization?.domains)].sort(
    (a, b) =>
      normalizeEmailDomain(a.emailDomain).localeCompare(
        normalizeEmailDomain(b.emailDomain)
      )
  );
  const hasMoreDomains =
    organization.workosOrganization?.domains.pageInfo.hasNextPage ?? false;
  const domainTotalCount =
    organization.workosOrganization?.domains.totalCount ?? 0;
  const parent = useMemo(
    () =>
      tenants.find(
        (tenant) =>
          tenant.luminaryOrganization?.id === organization.id &&
          tenant.orgMembershipType === TenantOrgMembershipType.Parent
      ) ?? null,
    [organization.id, tenants]
  );
  const memberTenants = useMemo(
    () =>
      tenants
        .filter(
          (tenant) =>
            tenant.luminaryOrganization?.id === organization.id &&
            tenant.orgMembershipType === TenantOrgMembershipType.Member
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [organization.id, tenants]
  );

  const [newDomain, setNewDomain] = useState('');
  const [workosConnectionId, setWorkosConnectionId] = useServerSyncedInput(
    organization.workosOrganization?.workosConnectionID ?? ''
  );
  const [workosDirectoryId, setWorkosDirectoryId] = useServerSyncedInput(
    organization.workosOrganization?.workosDirectoryID ?? ''
  );
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(
    null
  );
  const [isCreateMemberTenantModalOpen, setIsCreateMemberTenantModalOpen] =
    useState(false);

  const mutationOptions = (successMessage: string, errorMessage: string) => ({
    refetchQueries: [Operations.Query.AdminOrganizationsPage],
    awaitRefetchQueries: true,
    onCompleted: () => {
      showFeedback(successMessage, { variant: 'success' as const });
    },
    onError: (error: Error) => {
      showFeedback(errorMessage);
      reportError(errorMessage, error);
    },
  });

  const [setJit, { loading: settingJit }] =
    useAdminOrganizationsPageSetLuminaryOrganizationJitMutation(
      mutationOptions('JIT setting updated', 'Could not update JIT setting')
    );
  const [addDomain, { loading: addingDomain }] =
    useAdminOrganizationsPageAddWorkOsOrganizationDomainMutation(
      mutationOptions('Domain added', 'Could not add domain')
    );
  const [deleteDomain, { loading: deletingDomain }] =
    useAdminOrganizationsPageDeleteWorkOsOrganizationDomainMutation(
      mutationOptions('Domain removed', 'Could not remove domain')
    );
  const [updateWorkos, { loading: updatingWorkos }] =
    useAdminOrganizationsPageUpdateWorkOsOrganizationMutation(
      mutationOptions(
        'WorkOS configuration updated',
        'Could not update WorkOS configuration'
      )
    );

  const handleAddDomain = async () => {
    const emailDomain = normalizeEmailDomain(newDomain);
    if (!canEdit || !emailDomain || !organization.workosOrganization) return;
    const result = await addDomain({
      variables: {
        input: {
          emailDomain,
          workosOrganizationID: organization.workosOrganization.id,
        },
      },
    });
    if (result.data?.addWorkOSOrganizationDomain) setNewDomain('');
  };

  const handleSaveWorkos = async () => {
    if (!canEdit || !organization.workosOrganization) return;
    const connectionId = workosConnectionId.trim();
    const directoryId = workosDirectoryId.trim();
    await updateWorkos({
      variables: {
        id: organization.workosOrganization.id,
        input: {
          workosConnectionID: connectionId || undefined,
          clearWorkosConnectionID: !connectionId,
          workosDirectoryID: directoryId || undefined,
          clearWorkosDirectoryID: !directoryId,
        },
      },
    });
  };

  const handleRemovalConfirmation = async (reason: 'confirm' | 'cancel') => {
    if (reason === 'cancel' || !pendingRemoval || !canEdit) {
      setPendingRemoval(null);
      return;
    }
    const result = await deleteDomain({
      variables: { id: pendingRemoval.id },
    });
    if (result.data?.deleteWorkOSOrganizationDomain) {
      setPendingRemoval(null);
    }
  };

  return (
    <>
      <Stack spacing={2}>
        {!canEdit && (
          <Callout severity="info-high">
            You can view this organization&apos;s settings here. To edit them,
            log in to its parent tenant{parent ? `, ${parent.name}` : ''}.
          </Callout>
        )}

        <HeaderCard heading="Tenant management">
          <Stack spacing={2} maxWidth={700}>
            <Callout severity="info-low">
              Parent and member tenant membership is set when each tenant is
              created and cannot be changed.
            </Callout>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Typography variant="title-s">Parent tenant</Typography>
              <Stack spacing={0.25} alignItems="flex-end">
                <Typography variant="title-xs">
                  {parent?.name ?? 'Not found'}
                </Typography>
                {parent && (
                  <Typography variant="body-xs">{parent.subdomain}</Typography>
                )}
              </Stack>
            </Stack>

            <Divider />

            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="title-s">
                  Member tenants ({memberTenants.length})
                </Typography>
                <Tooltip
                  title="Member tenants can only be created through an organization's parent tenant."
                  show={!canEdit}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!canEdit}
                    onClick={() => setIsCreateMemberTenantModalOpen(true)}
                  >
                    Create member tenant
                  </Button>
                </Tooltip>
              </Stack>
              <OrganizationDetailsList
                items={memberTenants}
                emptyMessage="No member tenants have been added."
                getItemKey={(tenant) => tenant.id}
                getSearchableText={(tenant) =>
                  `${tenant.name} ${tenant.subdomain}`
                }
                itemName="member tenants"
                searchLabel="Search member tenants"
                renderItem={(tenant) => (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ px: 1.5, py: 1 }}
                  >
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography variant="title-xs">{tenant.name}</Typography>
                      <Typography variant="body-xs">
                        {tenant.subdomain}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              />
            </Stack>
          </Stack>
        </HeaderCard>

        <HeaderCard heading="Auth and provisioning">
          <Stack spacing={3} maxWidth={700}>
            <Stack spacing={1}>
              <Typography variant="title-s">JIT provisioning</Typography>
              <Typography variant="body-xs">
                Just-in-time access applies to the entire organization and all
                of its tenants.
              </Typography>
              <Switch
                label="JIT access"
                labelPosition="right"
                name="organizationJit"
                value={Boolean(organization.jitEnabledAt)}
                disabled={!canEdit || settingJit}
                onChange={(event) => {
                  void setJit({
                    variables: {
                      input: {
                        id: organization.id,
                        enabled: event.target.checked,
                      },
                    },
                  });
                }}
              />
            </Stack>

            <Divider />

            <Stack spacing={2}>
              <Typography variant="title-s">
                SSO domains ({domains.length})
              </Typography>
              {hasMoreDomains && (
                <Callout severity="warning">
                  Showing {domains.length} of {domainTotalCount} SSO domains.
                  Some domains are not listed.
                </Callout>
              )}
              {canEdit && (
                <Stack spacing={1}>
                  <TextInput
                    label="Add domain"
                    name="newDomain"
                    value={newDomain}
                    placeholder="acme.com"
                    helpText="Enter the domain without the @ symbol."
                    onChange={(event) => setNewDomain(event.target.value)}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    loading={addingDomain}
                    disabled={
                      !newDomain.trim() || !organization.workosOrganization
                    }
                    onClick={handleAddDomain}
                  >
                    Add domain
                  </Button>
                </Stack>
              )}
              <OrganizationDetailsList
                items={domains}
                emptyMessage="No routing domains configured."
                getItemKey={(domain) => domain.id}
                getSearchableText={(domain) => domain.emailDomain}
                itemName="SSO domains"
                searchLabel="Search SSO domains"
                renderItem={(domain) => {
                  const domainLabel = normalizeEmailDomain(domain.emailDomain);
                  return (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ px: 1.5, py: 1 }}
                    >
                      <Typography variant="body-xs">{domainLabel}</Typography>
                      {canEdit && (
                        <Button
                          aria-label={`Remove ${domainLabel}`}
                          variant="transparent"
                          size="xs"
                          square
                          disabled={deletingDomain}
                          onClick={() =>
                            setPendingRemoval({
                              id: domain.id,
                              label: domainLabel,
                            })
                          }
                        >
                          <Trash01Icon size={16} />
                        </Button>
                      )}
                    </Stack>
                  );
                }}
              />
            </Stack>

            <Divider />

            <Stack spacing={2}>
              <Typography variant="title-s">WorkOS configuration</Typography>
              <Typography variant="body-xs">
                WorkOS organization ID:{' '}
                {organization.workosOrganization?.externalID ?? '—'}
              </Typography>
              <TextInput
                label="SSO connection ID"
                name="workosConnectionId"
                value={workosConnectionId}
                placeholder="conn_..."
                disabled={!canEdit}
                onChange={(event) => setWorkosConnectionId(event.target.value)}
              />
              <TextInput
                label="SCIM directory ID"
                name="workosDirectoryId"
                value={workosDirectoryId}
                placeholder="directory_..."
                disabled={!canEdit}
                onChange={(event) => setWorkosDirectoryId(event.target.value)}
              />
              {canEdit && (
                <Button
                  variant="primary"
                  size="sm"
                  loading={updatingWorkos}
                  disabled={!organization.workosOrganization}
                  onClick={handleSaveWorkos}
                >
                  Save WorkOS configuration
                </Button>
              )}
            </Stack>
          </Stack>
        </HeaderCard>

        <HeaderCard heading="Application configuration">
          <OrganizationConfigurationForm
            luminaryOrganizationID={organization.id}
            readOnly={!canEdit}
          />
        </HeaderCard>

        <HeaderCard heading="Presentations">
          <OrganizationPresentationSettingsForm
            luminaryOrganizationID={organization.id}
            readOnly={!canEdit}
          />
        </HeaderCard>
      </Stack>

      {pendingRemoval && (
        <ConfirmationModal
          isOpen
          heading="Remove SSO domain?"
          confirmText="Remove"
          confirmButtonProps={{
            variant: 'destructive-primary',
            loading: deletingDomain,
          }}
          onClose={handleRemovalConfirmation}
        >
          Remove {pendingRemoval.label} from {organization.displayName}?
        </ConfirmationModal>
      )}

      {isCreateMemberTenantModalOpen && (
        <CreateOrganizationMemberTenantModal
          isOpen
          onClose={() => setIsCreateMemberTenantModalOpen(false)}
        />
      )}
    </>
  );
}
